import { computed, ref, watch, type Ref } from 'vue';
import { refDebounced } from '@vueuse/core';
import { toast } from 'vue-sonner';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import {
  useSharedSavedViewsQuery,
  useDeleteSharedSavedView,
  useImportSharedSavedView,
} from '@/composables/api/useSharedSavedViewsApi';
import { useCursorPager, type PagerDirection } from '@/composables/useCursorPager';
import { buildShareUrl, copyShareToClipboard } from '@/utils/savedViewSharing';
import type { SharedSavedViewRecord } from '@/utils/savedViews';

/** Rows per page. Mirrors the server's own default; the server is the authority. */
const PAGE_SIZE = 50;

/**
 * Wiring for [BrowseSharedViewsDialog](src/components/BrowseSharedViewsDialog.vue) —
 * the library of views other people have shared (SAU-310).
 *
 * Same split as `useSavedViews`: the dialog renders and emits, this owns every
 * toast and every mutation. The list is scoped strictly to the current page's
 * entity with no widen toggle — a Live Track view is no use on the page you are
 * standing on — and the server filters it by model permission regardless, since
 * the entity travels as a request parameter and is therefore not a trustworthy
 * constraint on its own.
 *
 * @param isOpen gates the query, so opening the dialog is what fetches. The
 *   library is the only read here that hits a LIKE, and it is not needed to
 *   render the toolbar.
 */
export function useSharedSavedViewLibrary(isOpen: Ref<boolean>) {
  const store = usePersistentTableStore();

  const entityId = computed(() => store.storeKey);
  const search = ref('');
  // The query keys on the DEBOUNCED term. Keying on the raw ref fired one GET
  // per keystroke — fourteen LIKE scans plus a correlated users subquery for
  // "online players" — and flashed the spinner between characters.
  const debouncedSearch = refDebounced(search, 250);
  // Typing is debounced; CLEARING is immediate. The dialog resets the box on
  // open, and a debounced-only term made the list show the previous search's
  // "no match" for a quarter of a second, then refetch that stale term first.
  const queryTerm = computed(() => (search.value === '' ? '' : debouncedSearch.value));

  /**
   * Prev/Next over the library, on the shared pager the searchable tables use.
   *
   * Only First/Prev/Next are wired. The pager can also ask for a LAST page, which
   * the Elasticsearch-backed lists serve with a reverse-sort trick — this endpoint
   * is a plain MySQL cursor and has no such trick, so no Last button is rendered
   * and `navigate('last')` is never reached. The page count in the label comes from
   * the total, which the server sends with the first page of every result set.
   */
  const pager = useCursorPager(ref(PAGE_SIZE));
  const cursor = computed(() => pager.cursor.value.token);

  // A new result set starts at its own first page. Without this, narrowing the
  // search while on page 3 asked for page 3 of the OLD set: the cursor is a row
  // position, not a page number, so it lands somewhere arbitrary in the new
  // results — or past the end of them, which reads as "no shared views match" for
  // a term that matches plenty.
  watch([entityId, queryTerm], () => pager.reset());

  const libraryQuery = useSharedSavedViewsQuery(entityId, queryTerm, cursor, isOpen);
  const deleteMutation = useDeleteSharedSavedView();
  const importMutation = useImportSharedSavedView();

  const sharedViews = computed<SharedSavedViewRecord[]>(() => libraryQuery.data.value?.rows ?? []);

  // Fed from every settled response, so Prev/Next enable off tokens the server
  // actually returned rather than off an inferred page count.
  watch(
    () => libraryQuery.data.value,
    (page) => {
      if (!page) {
        return;
      }

      // An empty page that is NOT the first one means the rows it held are gone —
      // the usual way being that you just deleted the last of them. Go back to the
      // start rather than sitting on it.
      //
      // Sitting on it is not merely untidy, it is a dead end: Laravel's cursor
      // paginator returns null for BOTH previousCursor() and nextCursor() when a page
      // comes back empty, so hasPrev and hasNext both go false and the pager bar —
      // rendered behind `v-if="hasPrevPage || hasNextPage"` — disappears instead of
      // greying out. The dialog then reads "Nobody has shared a view for this page
      // yet." over a library that still holds fifty, with no control to leave it.
      // Closing and reopening was the only way back.
      //
      // This cannot loop: reset() puts pageNumber at 1, which fails the test below.
      if (page.rows.length === 0 && pager.pageNumber.value > 1) {
        pager.reset();
        return;
      }

      pager.captureMeta(page.meta);
    },
  );

  /**
   * Which rows have an action in flight. A SET, not a single id.
   *
   * It used to be one id, and both the guards below and every row's buttons
   * read it as "is ANYTHING busy" — so importing one entry greyed out Import on
   * all eight rows and Delete on every row you own, with only the row you
   * clicked saying why. One row's request is no reason to freeze the others,
   * and the cache layer does not need it to be: the delete rollback is additive
   * per row and the import has no optimistic patch at all.
   *
   * What the guard still earns is blocking a second action on the SAME row — a
   * double-clicked Import posts twice and the server hands back a suffixed
   * duplicate.
   */
  const pendingSharedViewIds = ref<Set<number>>(new Set());

  /** True while this one row has an import or a delete in flight. */
  function isSharedViewPending(sharedSavedViewId: number): boolean {
    return pendingSharedViewIds.value.has(sharedSavedViewId);
  }

  const handlers = {
    /**
     * Fork a library entry into the caller's own saved views. Everything is a
     * fork: the copy is independent, gets its own share key if the recipient
     * shares it onward, and nothing tracks anything afterwards.
     */
    async onImport(sharedView: SharedSavedViewRecord): Promise<void> {
      if (isSharedViewPending(sharedView.id)) {
        return;
      }
      pendingSharedViewIds.value.add(sharedView.id);
      try {
        const imported = await importMutation.mutateAsync({
          entity: entityId.value,
          shareKey: sharedView.share_key,
        });
        // The server suffixes a name the recipient already uses, so report what
        // actually landed rather than what they clicked on.
        toast.success(
          imported.name === sharedView.name
            ? `Imported "${imported.name}".`
            : `Imported as "${imported.name}" — you already had a view called "${sharedView.name}".`,
        );
      } catch {
        // importMutation.onError already toasted.
      } finally {
        pendingSharedViewIds.value.delete(sharedView.id);
      }
    },

    /** Copy a library entry's link without importing it. */
    async onCopyLink(sharedView: SharedSavedViewRecord): Promise<void> {
      const url = buildShareUrl(window.location.origin, sharedView.share_key);
      try {
        await copyShareToClipboard(sharedView.name, url);
        toast.success('Share link copied.');
      } catch {
        toast.error(`Could not copy the link. It is: ${url}`);
      }
    },

    /** Remove an entry the policy lets you remove. */
    async onDelete(sharedView: SharedSavedViewRecord): Promise<void> {
      // `can_delete` is the policy's answer — your own rows, or any row if you
      // are a developer, so a departed publisher's entries can be tidied away.
      if (!sharedView.can_delete || isSharedViewPending(sharedView.id)) {
        return;
      }
      pendingSharedViewIds.value.add(sharedView.id);
      try {
        await deleteMutation.mutateAsync({
          entity: entityId.value,
          search: queryTerm.value,
          cursor: cursor.value,
          sharedSavedViewId: sharedView.id,
        });
        toast.success(`Removed "${sharedView.name}" from the library.`);
      } catch {
        // deleteMutation.onError already toasted.
      } finally {
        pendingSharedViewIds.value.delete(sharedView.id);
      }
    },
  };

  return {
    search,
    sharedViews,
    isLoading: computed(() => libraryQuery.isLoading.value),
    isError: computed(() => libraryQuery.isError.value),
    isSharedViewPending,
    /**
     * Rows in the WHOLE result set, not on this page.
     *
     * The heading used to count the rows it had in hand, which was the same number
     * while the listing returned everything and stopped being one the moment it
     * returned a page — "50 shared views" on every page of a library of two hundred.
     */
    total: pager.cachedTotal,
    /** 1-based, for the "Page 2 of 5" label. */
    pageNumber: pager.pageNumber,
    lastPage: pager.lastPage,
    hasNextPage: pager.hasNext,
    hasPrevPage: pager.hasPrev,
    /** True while a page hop is in flight; the previous page's rows stay on screen. */
    isChangingPage: computed(() => libraryQuery.isFetching.value && !libraryQuery.isLoading.value),
    goToPage: (direction: PagerDirection) => pager.navigate(direction),
    handlers,
  };
}
